/**
 * Request schemas shared by the API (validation) and the client (form rules).
 * Every money field is integer cents; every date-only field is "YYYY-MM-DD".
 */
import { z } from 'zod';
import { ACCOUNT_SUBTYPES, ACCOUNT_TYPES, EVENT_STATUSES, PAYMENT_METHODS, PAYMENT_TERMS } from './enums.js';
export const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date in YYYY-MM-DD form.')
    .refine((s) => !Number.isNaN(Date.parse(`${s}T12:00:00Z`)), 'That is not a real date.');
export const optionalIsoDate = z
    .union([isoDate, z.literal(''), z.null(), z.undefined()])
    .transform((v) => (v ? v : null));
export const cents = z.number().int('Amounts are whole cents.');
export const positiveCents = cents.positive('Enter an amount greater than zero.');
export const nonNegativeCents = cents.min(0, 'Amount cannot be negative.');
export const email = z.string().trim().toLowerCase().email('Enter a valid email address.').max(254);
export const password = z.string().min(8, 'Use at least 8 characters.').max(200);
const trimmed = (max = 200) => z.string().trim().max(max);
/** Missing stays missing (so partial updates merge), empty becomes null. */
const optionalText = (max = 200) => z
    .union([trimmed(max), z.null(), z.undefined()])
    .transform((v) => (v === undefined ? undefined : v ? v : null));
// ── Auth ────────────────────────────────────────────────────────────────────
export const signupSchema = z.object({
    email,
    password,
    full_name: trimmed(120).min(1, 'Enter your name.'),
});
export const loginSchema = z.object({ email, password: z.string().min(1, 'Enter your password.') });
export const forgotSchema = z.object({ email });
export const tokenPasswordSchema = z.object({ token: z.string().min(20).max(200), password });
export const changePasswordSchema = z.object({
    current_password: z.string().min(1, 'Enter your current password.'),
    new_password: password,
});
export const profileSchema = z.object({
    full_name: optionalText(120),
    display_name: optionalText(120),
    phone: optionalText(30),
    address: optionalText(200),
    city: optionalText(100),
    state: optionalText(50),
    zip: optionalText(20),
});
/** The Complete Profile gate: every one of these must be present. */
export const PROFILE_REQUIRED_FIELDS = ['full_name', 'phone', 'address', 'city', 'state', 'zip'];
// ── Events ──────────────────────────────────────────────────────────────────
export const dailyTimeSchema = z.object({
    date: isoDate,
    start_time: z.string().max(10).default(''),
    end_time: z.string().max(10).default(''),
});
export const ticketOptionInputSchema = z.object({
    id: z.string().uuid().optional(),
    name: trimmed(120).min(1, 'Each ticket option needs a name.'),
    description: optionalText(500),
    price_cents: nonNegativeCents,
    total_tickets: z.number().int().min(1, 'Each ticket option needs at least one ticket.'),
    valid_date: optionalIsoDate,
});
export const eventInputSchema = z.object({
    title: trimmed(200).min(1, 'Give the event a title.'),
    description: z.string().max(20_000).default(''),
    start_date: isoDate,
    end_date: optionalIsoDate,
    same_time_all_days: z.boolean().default(true),
    start_time: z.string().max(10).default(''),
    end_time: z.string().max(10).default(''),
    daily_times: z.array(dailyTimeSchema).default([]),
    venue_name: optionalText(200),
    venue_address: optionalText(200),
    venue_city: optionalText(100),
    venue_state: optionalText(50),
    venue_zip: optionalText(20),
    venue_logo_file_id: z.string().uuid().nullable().optional(),
    image_file_id: z.string().uuid().nullable().optional(),
    video_embed_urls: z.array(z.string().trim().url().max(500)).default([]),
    price_cents: nonNegativeCents.default(0),
    total_tickets: z.number().int().min(0).nullable().default(null),
    status: z.enum(EVENT_STATUSES).default('draft'),
    category: optionalText(80),
    income_account_id: z.string().uuid({ message: 'Choose an income account for this event.' }),
    require_attendee_names: z.boolean().default(false),
    admin_only: z.boolean().default(false),
    ticket_options: z.array(ticketOptionInputSchema).default([]),
});
// ── Coupons ─────────────────────────────────────────────────────────────────
export const couponInputSchema = z
    .object({
    event_id: z.string().uuid({ message: 'Please select an event.' }),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(1, 'Coupon code is required.')
        .max(40)
        .regex(/^[A-Z0-9_-]+$/, 'Use letters, numbers, dashes or underscores.'),
    description: optionalText(200),
    applies_to: z.enum(['all', 'specific']).default('all'),
    ticket_option_id: z.string().uuid().nullable().optional(),
    discount_type: z.enum(['percentage', 'dollar']),
    /** Percent (1–100) for percentage coupons, cents for dollar coupons. */
    discount_value: z.number().int().positive('Please enter a valid discount value.'),
    active: z.boolean().default(true),
    max_uses: z.number().int().positive().nullable().default(null),
})
    .superRefine((c, ctx) => {
    if (c.discount_type === 'percentage' && (c.discount_value < 1 || c.discount_value > 100)) {
        ctx.addIssue({ code: 'custom', path: ['discount_value'], message: 'Percentage must be between 1 and 100.' });
    }
    if (c.applies_to === 'specific' && !c.ticket_option_id) {
        ctx.addIssue({ code: 'custom', path: ['ticket_option_id'], message: 'Please select a ticket option.' });
    }
});
// ── Checkout ────────────────────────────────────────────────────────────────
export const attendeeNameSchema = z.object({
    first_name: trimmed(80).min(1),
    last_name: trimmed(80).min(1),
});
export const checkoutBaseSchema = z.object({
    event_id: z.string().uuid(),
    ticket_option_id: z.string().uuid().nullable().optional(),
    quantity: z.number().int().min(1).max(100),
    coupon_code: z.string().trim().toUpperCase().max(40).nullable().optional(),
    attendee_names: z.array(attendeeNameSchema).nullable().optional(),
});
export const cardCheckoutSchema = checkoutBaseSchema.extend({
    source_id: z.string().min(1, 'The card form did not produce a token.').max(200),
});
/** Admin-assisted sales may name the actual customer; otherwise the signed-in admin is the buyer (legacy behaviour). */
const customerFields = {
    customer_name: optionalText(120),
    customer_email: z.union([email, z.literal(''), z.null(), z.undefined()]).transform((v) => (v ? v : null)),
};
export const achCheckoutSchema = checkoutBaseSchema.extend({
    ...customerFields,
    bank_name: trimmed(120).min(1, 'Enter the bank name.'),
    routing_number: z.string().regex(/^\d{9}$/, 'Routing number must be exactly 9 digits.'),
    account_number: z.string().regex(/^\d{4,17}$/, 'Account number must be 4 to 17 digits.'),
});
export const inPersonCheckoutSchema = checkoutBaseSchema.extend({
    ...customerFields,
    in_person_type: z.enum(['cash', 'check']),
    check_number: z.string().trim().max(20).nullable().optional(),
});
export const couponValidateSchema = z.object({
    event_id: z.string().uuid(),
    ticket_option_id: z.string().uuid().nullable().optional(),
    quantity: z.number().int().min(1).max(100),
    code: z.string().trim().toUpperCase().min(1).max(40),
});
export const paymentMethodsSchema = z.object({
    card: z.boolean(),
    ach: z.boolean(),
    cash: z.boolean(),
    check: z.boolean(),
});
// ── Accounting ──────────────────────────────────────────────────────────────
export const accountInputSchema = z.object({
    name: trimmed(120).min(1, 'Account name is required.'),
    type: z.enum(ACCOUNT_TYPES),
    subtype: z.enum(ACCOUNT_SUBTYPES).default('other'),
    description: optionalText(300),
    parent_id: z.string().uuid().nullable().optional(),
    event_id: z.string().uuid().nullable().optional(),
    number: optionalText(20),
});
const digits = (pattern, message) => z.union([z.string().regex(pattern, message), z.literal(''), z.null(), z.undefined()]);
export const bankAccountInputSchema = z.object({
    name: trimmed(120).min(1, 'Account name is required.'),
    description: optionalText(300),
    bank_name: optionalText(120),
    account_holder: optionalText(120),
    routing_number: digits(/^\d{9}$/, 'Routing number must be exactly 9 digits.'),
    account_number: digits(/^\d{4,17}$/, 'Account number must be 4 to 17 digits.'),
    next_check_number: z.number().int().min(1).optional(),
    check_template_id: z.string().uuid().nullable().optional(),
    inactive: z.boolean().optional(),
});
export const vendorInputSchema = z.object({
    name: trimmed(160).min(1, 'Vendor name is required.'),
    contact_name: optionalText(120),
    address: optionalText(200),
    city: optionalText(100),
    state: optionalText(50),
    zip: optionalText(20),
    phone: optionalText(30),
    mobile: optionalText(30),
    fax: optionalText(30),
    email: z.union([email, z.literal(''), z.null(), z.undefined()]).transform((v) => (v ? v : null)),
    default_account_id: z.string().uuid().nullable().optional(),
    payment_terms: z
        .union([z.enum(PAYMENT_TERMS), z.literal(''), z.null(), z.undefined()])
        .transform((v) => (v ? v : null)),
    is_1099: z.boolean().optional(),
    inactive: z.boolean().optional(),
});
export const billInputSchema = z.object({
    vendor_id: z.string().uuid().nullable().optional(),
    vendor_name: trimmed(160).min(1, 'Vendor is required.'),
    address: optionalText(200),
    city: optionalText(100),
    state: optionalText(50),
    zip: optionalText(20),
    terms: optionalText(40),
    account_id: z.string().uuid({ message: 'Please select a Chart of Account before saving the bill.' }),
    bill_date: isoDate,
    due_date: optionalIsoDate,
    ref_no: optionalText(80),
    amount_cents: positiveCents,
    memo: optionalText(500),
    attachment_file_ids: z.array(z.string().uuid()).default([]),
});
export const billPaymentInputSchema = z
    .object({
    payment_date: isoDate,
    method: z.enum(['check', 'credit_card']),
    bank_account_id: z.string().uuid().nullable().optional(),
    card_account_id: z.string().uuid().nullable().optional(),
    check_mode: z.enum(['print', 'assign', 'handwritten']).default('print'),
    starting_check_number: z.number().int().min(1).nullable().optional(),
    allocations: z
        .array(z.object({ bill_id: z.string().uuid(), amount_cents: positiveCents }))
        .min(1, 'Select at least one bill.'),
})
    .superRefine((p, ctx) => {
    if (p.method === 'check' && !p.bank_account_id) {
        ctx.addIssue({ code: 'custom', path: ['bank_account_id'], message: 'Choose a checking account.' });
    }
    if (p.method === 'credit_card' && !p.card_account_id) {
        ctx.addIssue({ code: 'custom', path: ['card_account_id'], message: 'Choose a credit card.' });
    }
});
export const journalLineInputSchema = z
    .object({
    account_id: z.string().uuid(),
    debit_cents: nonNegativeCents.default(0),
    credit_cents: nonNegativeCents.default(0),
    memo: optionalText(300),
})
    .refine((l) => (l.debit_cents > 0) !== (l.credit_cents > 0), 'Each line needs a debit or a credit, not both.');
export const journalEntryInputSchema = z
    .object({
    entry_date: isoDate,
    memo: optionalText(300),
    lines: z.array(journalLineInputSchema).min(2, 'A journal entry needs at least two lines.'),
})
    .refine((e) => e.lines.reduce((s, l) => s + l.debit_cents, 0) === e.lines.reduce((s, l) => s + l.credit_cents, 0), 'Debits must equal credits.');
export const registerEntryInputSchema = z.object({
    entry_date: isoDate,
    kind: z.enum(['payment', 'deposit']),
    amount_cents: positiveCents,
    other_account_id: z.string().uuid({ message: 'Choose an account.' }),
    payee: optionalText(160),
    memo: optionalText(300),
    payment_type: z.enum(['ach', 'card', 'cash', 'check', 'deposit', 'other']).default('other'),
    transaction_number: optionalText(40),
});
export const depositMarkSchema = z.object({
    bank_account_id: z.string().uuid({ message: 'Select a checking account.' }),
    bank_date: isoDate,
    merchant_fees_cents: nonNegativeCents.optional(),
});
export const receiptInputSchema = z.object({
    payer_name: trimmed(160).min(1, 'Customer name is required.'),
    reference: trimmed(80).min(1, 'Transaction ID is required.'),
    amount_cents: positiveCents,
    income_account_id: z.string().uuid({ message: 'Choose an income account.' }),
    received_on: isoDate,
    memo: optionalText(300),
    event_id: z.string().uuid().nullable().optional(),
    quantity: z.number().int().min(1).default(1),
});
export const reconciliationStartSchema = z.object({
    account_id: z.string().uuid(),
    statement_date: isoDate,
    ending_cents: cents,
});
export const paymentMethodEnum = z.enum(PAYMENT_METHODS);
//# sourceMappingURL=schemas.js.map